import { LOCATION_PREFERENCE } from './services/LocationService';
import { useDayForIt, venueKindFor } from './hooks/useDayForIt';
import ShareButton from './components/ShareButton';
import RegionNotice from './components/RegionNotice';
import VerdictGraphic from './components/VerdictGraphic';
import VenueSection from './components/VenueSection';
import DebugControls from './components/DebugControls';
import {
  HOMEPAGE_DESCRIPTION,
  HOMEPAGE_META,
  HOMEPAGE_SCHEMA,
  HOMEPAGE_TITLE,
  buildPageMeta,
} from './content/siteMetadata';
import { brandFromPath, useBodyClass, useTheme } from './context/ThemeProvider';
import { DEFAULT_THEME, themes } from './config/themes';
import { trackEvent } from './utils/analytics';

const LOCATION_PROMPT_TEXT = {
  [LOCATION_PREFERENCE.allowed]: 'Using your saved location preference for nearby results.',
  [LOCATION_PREFERENCE.denied]: 'Location access is off. We are using Melbourne until you choose otherwise.',
};
const DEFAULT_LOCATION_PROMPT_TEXT =
  'Want local results? Let us use your location and remember that choice in this browser.';
const APPROXIMATE_LOCATION_PROMPT_TEXT =
  'Showing your rough area for now. Want nearby results? Let us use your location and remember that choice in this browser.';

/**
 * Homepage meta. Palette theme routes (e.g. /woolies) are variants of the homepage, so they
 * point their canonical at `/`; unlisted ones (sponsor demos) are also noindex.
 */
export const meta = ({ location }) => {
  const brand = brandFromPath(location.pathname);
  if (brand === DEFAULT_THEME) return HOMEPAGE_META;

  return buildPageMeta({
    title: HOMEPAGE_TITLE,
    description: HOMEPAGE_DESCRIPTION,
    path: '/',
    schema: HOMEPAGE_SCHEMA,
    noindex: Boolean(themes[brand].unlisted),
  });
};

function App() {
  const {
    status,
    weather,
    loading,
    error,
    location,
    locationSource,
    venue,
    locationPreference,
    requestingLocation,
    enableLocation,
    forceVerdict,
  } = useDayForIt();
  const { theme } = useTheme();

  useBodyClass(status ? (status.isDayForIt ? 'day-for-it' : 'pub-time') : '');

  const isUsingSavedLocation = locationPreference === LOCATION_PREFERENCE.allowed;
  const locationPromptText = LOCATION_PROMPT_TEXT[locationPreference]
    ?? (locationSource === 'approximate' ? APPROXIMATE_LOCATION_PROMPT_TEXT : DEFAULT_LOCATION_PROMPT_TEXT);

  return (
    <>
      {/* Region Notice - EU only (GDPR compliance) */}
      <RegionNotice />

      <main className="container" aria-busy={loading ? 'true' : undefined}>
        <h1>Day For It?</h1>
        <p className="visually-hidden">
          Check whether today is a day for park beers or pub beers with live weather,
          nearby park and pub suggestions, and city-specific forecast guides for major
          Australian cities.
        </p>

        {loading ? (
          <p className="loading" role="status" aria-live="polite">
            Checking the vibes...
          </p>
        ) : error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : (
          <div className="status-card">
            <VerdictGraphic isDayForIt={Boolean(status?.isDayForIt)} />
            <h2>{status?.isDayForIt ? theme.texts.dayTitle : theme.texts.notDayTitle}</h2>
            <p className="reason">{status?.reason}</p>

            {weather && (
              <div className="weather-info">
                <p>{weather.temperature_2m}°C</p>
                <p>Wind: {weather.wind_speed_10m} km/h</p>
              </div>
            )}

            <div className="location-preference-panel">
              <p className="location-preference-copy">{locationPromptText}</p>
              {!isUsingSavedLocation && (
                <button
                  type="button"
                  className="location-preference-button"
                  onClick={enableLocation}
                  disabled={requestingLocation}
                >
                  {requestingLocation ? 'Checking location...' : 'Use my location'}
                </button>
              )}
            </div>

            <ShareButton status={status} weather={weather} />

            {/* Buy Me a Coffee */}
            <a
              href="https://buymeacoffee.com/dayforit"
              target="_blank"
              rel="noopener noreferrer"
              className="buy-me-coffee"
              onClick={() => trackEvent('support_click', { method: 'buy_me_a_coffee' })}
            >
              🍺 Shout me One
            </a>
          </div>
        )}

        {!loading && !error && (
          <VenueSection kind={venueKindFor(status)} venue={venue} userLocation={location} />
        )}

        {import.meta.env.MODE === 'development' && (
          <DebugControls onForceVerdict={forceVerdict} />
        )}
      </main>
    </>
  );
}

export default App;
